/**
 * Searchable single-select combobox for dashboard filters (province / city / client).
 * Value `'all'` means no filter; options are a flat string list.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';

export interface FilterComboboxProps {
  id: string;
  label?: string;
  value: 'all' | string;
  onChange: (next: 'all' | string) => void;
  options: readonly (string | { value: string; label: string })[];
  placeholder?: string;
  allRowLabel?: string;
  disabled?: boolean;
  /** Optional class on the outer wrapper */
  className?: string;
  /** Optional custom matcher for option search (e.g. province aliases). */
  optionMatchesQuery?: (option: string, query: string) => boolean;
  /**
   * When true, listbox is portaled to `document.body` with fixed positioning so it is not
   * clipped by modal overflow (matches dashboard typing UX while staying above dialogs).
   */
  usePortal?: boolean;
  /** z-index for portaled listbox (default 100000). */
  portalZIndex?: number;
  /**
   * When true, pressing Enter with a non-empty query commits the trimmed query as the value
   * even if it does not appear in options (e.g. free-text role).
   */
  allowFreeText?: boolean;
  /** Extra classes for the listbox panel (max-height, etc.). */
  listboxClassName?: string;
  /** Max height (px) for the portaled listbox panel (default 320). */
  portalMaxHeightPx?: number;
}

/** Only one combobox listbox open at a time — stacked inline panels block Search/actions below. */
const closeById = new Map<string, () => void>();
let openComboboxId: string | null = null;

function closeOtherComboboxes(exceptId: string) {
  if (openComboboxId && openComboboxId !== exceptId) {
    closeById.get(openComboboxId)?.();
  }
  openComboboxId = exceptId;
}

function clearOpenCombobox(exceptId: string) {
  if (openComboboxId === exceptId) {
    openComboboxId = null;
  }
}

export function FilterCombobox({
  id,
  label,
  value,
  onChange,
  options,
  placeholder = 'Type to search…',
  allRowLabel = 'All (no filter)',
  disabled = false,
  className = '',
  optionMatchesQuery,
  usePortal = false,
  portalZIndex = 100000,
  allowFreeText = false,
  listboxClassName = '',
  portalMaxHeightPx = 320,
}: FilterComboboxProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerWrapRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const [panelStyle, setPanelStyle] = useState<React.CSSProperties>({});
  const isActive = value !== 'all';

  const closeDropdown = useCallback(() => {
    clearOpenCombobox(id);
    setOpen(false);
    setQuery('');
    setEditing(false);
  }, [id]);

  const openDropdown = useCallback(() => {
    closeOtherComboboxes(id);
    setOpen(true);
  }, [id]);

  useEffect(() => {
    closeById.set(id, closeDropdown);
    return () => {
      closeById.delete(id);
      clearOpenCombobox(id);
    };
  }, [id, closeDropdown]);

  const normalizedOptions = useMemo(() => {
    return options
      .map((o) => (typeof o === 'string' ? { value: o, label: o } : o))
      .filter((o) => typeof o.value === 'string' && o.value.trim().length > 0);
  }, [options]);

  const hasAllOption = useMemo(() => normalizedOptions.some((o) => o.value === 'all'), [normalizedOptions]);

  const selectedLabel = useMemo(() => {
    if (value === 'all') return normalizedOptions.find((o) => o.value === 'all')?.label ?? allRowLabel;
    return normalizedOptions.find((o) => o.value === value)?.label ?? value;
  }, [normalizedOptions, value, allRowLabel]);

  const filtered = useMemo(() => {
    const effectiveQuery = open && editing ? query : '';
    const q = effectiveQuery.trim().toLowerCase();
    if (!q) return [...normalizedOptions];
    if (optionMatchesQuery) return normalizedOptions.filter((o) => optionMatchesQuery(o.label, query));
    return normalizedOptions.filter((o) => o.label.toLowerCase().includes(q));
  }, [normalizedOptions, open, editing, query, optionMatchesQuery]);

  const rows = useMemo(() => {
    const r: { kind: 'all' | 'opt'; label: string; value: 'all' | string }[] = [];
    if (!hasAllOption) r.push({ kind: 'all', label: allRowLabel, value: 'all' });
    for (const o of filtered) {
      r.push({ kind: 'opt', label: o.label, value: o.value });
    }
    return r;
  }, [filtered, allRowLabel, hasAllOption]);

  useEffect(() => {
    if (!open) return;
    setHighlighted(0);
  }, [open, query, options]);

  const updatePanelPosition = useCallback(() => {
    if (!usePortal || !triggerWrapRef.current) return;
    const rect = triggerWrapRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const panelWidth = Math.min(Math.max(rect.width, 200), vw - rect.left - 12);
    const maxPanel = portalMaxHeightPx;
    const spaceBelow = vh - rect.bottom;
    const openUp = spaceBelow < maxPanel + 16 && rect.top > maxPanel + 16;

    setPanelStyle({
      position: 'fixed',
      left: Math.max(8, Math.min(rect.left, vw - panelWidth - 8)),
      width: panelWidth,
      maxHeight: `min(${maxPanel}px, calc(100vh - 32px))`,
      overflowY: 'auto',
      zIndex: portalZIndex,
      ...(openUp
        ? { bottom: vh - rect.top + 4, top: 'auto' as const }
        : { top: rect.bottom + 4, bottom: 'auto' as const }),
    });
  }, [usePortal, portalZIndex, portalMaxHeightPx]);

  useEffect(() => {
    if (!usePortal || !open || disabled) return;
    updatePanelPosition();
    const onScrollOrResize = () => updatePanelPosition();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [usePortal, open, disabled, updatePanelPosition]);

  useEffect(() => {
    const onDocDown = (e: MouseEvent) => {
      const t = e.target as Node;
      const inTrigger = containerRef.current?.contains(t);
      const inList = listRef.current?.contains(t);
      if (!inTrigger && !inList) {
        closeDropdown();
      }
    };
    document.addEventListener('mousedown', onDocDown);
    return () => document.removeEventListener('mousedown', onDocDown);
  }, [closeDropdown]);

  const commit = useCallback(
    (next: 'all' | string) => {
      onChange(next);
      closeDropdown();
      inputRef.current?.blur();
    },
    [onChange, closeDropdown]
  );

  const displayInInput = open ? (editing ? query : selectedLabel) : selectedLabel;

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      openDropdown();
      setHighlighted((h) => Math.min(Math.max(0, rows.length - 1), h + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      openDropdown();
      setHighlighted((h) => Math.max(0, h - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (!open) {
        openDropdown();
        return;
      }
      const safeIdx = rows.length > 0 ? Math.min(highlighted, rows.length - 1) : 0;
      const row = rows[safeIdx];
      if (row) {
        commit(row.value);
        return;
      }
      if (allowFreeText && query.trim()) {
        commit(query.trim());
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      closeDropdown();
    }
  };

  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-row-index="${highlighted}"]`) as HTMLElement | null;
    el?.scrollIntoView?.({ block: 'nearest' });
  }, [highlighted, open]);

  const listboxContent = (
    <>
      {rows.map((row, idx) => (
        <button
          key={row.kind === 'all' ? '__all__' : row.value}
          type="button"
          role="option"
          data-row-index={idx}
          aria-selected={highlighted === idx}
          className={`block w-full px-3 py-2 text-left text-sm transition-colors focus:outline-none ${
            highlighted === idx
              ? 'bg-primary-100 text-primary-900 dark:bg-primary-900/40 dark:text-primary-100'
              : 'text-gray-900 hover:bg-gray-100 dark:text-slate-100 dark:hover:bg-slate-700'
          } ${idx > 0 ? 'border-t border-gray-100 dark:border-slate-700' : ''}`}
          onMouseDown={(e) => {
            e.preventDefault();
            commit(row.value);
          }}
          onMouseEnter={() => setHighlighted(idx)}
        >
          {row.label}
        </button>
      ))}
      {filtered.length === 0 && query.trim() !== '' && (
        <div className="border-t border-gray-100 px-3 py-2 text-xs text-gray-500 dark:border-slate-700 dark:text-slate-400">
          No matching options
        </div>
      )}
    </>
  );

  return (
    <div ref={containerRef} className={`relative ${open ? 'z-20' : 'z-0'} ${className}`}>
      {label ? (
        <label htmlFor={id} className="mb-1 block text-sm font-medium text-theme-text">
          {label}
        </label>
      ) : null}
      <div ref={triggerWrapRef} className="relative">
        <input
          ref={inputRef}
          id={id}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={`${id}-listbox`}
          aria-autocomplete="list"
          disabled={disabled}
          value={displayInInput}
          placeholder={value === 'all' && !open ? placeholder : undefined}
          onChange={(e) => {
            setEditing(true);
            setQuery(e.target.value);
            openDropdown();
            setHighlighted(0);
          }}
          onFocus={() => {
            openDropdown();
            setEditing(false);
            setQuery('');
            requestAnimationFrame(() => {
              if (selectedLabel) inputRef.current?.select();
            });
          }}
          onBlur={() => {
            window.setTimeout(() => {
              const active = document.activeElement;
              const inTrigger = containerRef.current?.contains(active);
              const inList = listRef.current?.contains(active);
              if (!inTrigger && !inList) {
                closeDropdown();
              }
            }, 0);
          }}
          onKeyDown={onKeyDown}
          className={[
            'w-full rounded-lg border border-theme-border bg-theme-paper px-3 py-2 pr-10 text-theme-text focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-60 dark:text-black',
            isActive
              ? 'bg-primary-50 border-primary-400 focus:border-primary-500 focus:ring-primary-500/30'
              : '',
          ].join(' ')}
        />
        <button
          type="button"
          tabIndex={-1}
          disabled={disabled}
          className="absolute inset-y-0 right-0 flex items-center px-2 text-theme-text-secondary hover:text-theme-text focus:outline-none disabled:opacity-40 border-0 bg-transparent shadow-none"
          aria-label="Open list"
          onMouseDown={(e) => {
            e.preventDefault();
            if (disabled) return;
            if (open) {
              closeDropdown();
            } else {
              openDropdown();
              setEditing(false);
              setQuery('');
            }
            inputRef.current?.focus();
            requestAnimationFrame(() => {
              if (selectedLabel) inputRef.current?.select();
            });
          }}
        >
          <ChevronDown className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      </div>
      {open &&
        !disabled &&
        (usePortal && typeof document !== 'undefined'
          ? createPortal(
              <div
                id={`${id}-listbox`}
                ref={listRef}
                role="listbox"
                style={panelStyle}
                className={`rounded-lg border border-gray-300 bg-white shadow-lg dark:border-slate-600 dark:bg-slate-800 ${listboxClassName}`}
                onMouseDown={(e) => e.stopPropagation()}
              >
                {listboxContent}
              </div>,
              document.body
            )
          : (
              <div
                id={`${id}-listbox`}
                ref={listRef}
                role="listbox"
                className={`absolute z-[1000] mt-1 max-h-56 w-full overflow-y-auto rounded-lg border border-gray-300 bg-white shadow-lg dark:border-slate-600 dark:bg-slate-800 ${listboxClassName}`}
                onMouseDown={(e) => e.stopPropagation()}
              >
                {listboxContent}
              </div>
            ))}
    </div>
  );
}
